import * as React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cn } from '@relay-towing/tokens';
import {
  toggleGroupItemRecipe,
  toggleGroupRecipe,
  type ToggleGroupItemRecipeProps,
  type ToggleGroupRecipeProps,
} from '../recipes/control.recipe';

/**
 * A segmented control: two or more options where exactly one (or any number,
 * with `type="multiple"`) is on.
 *
 *   <ToggleGroup type="single" value={view} onValueChange={setView}>
 *     <ToggleGroupItem value="grid" aria-label="Grid view"><Icon …/></ToggleGroupItem>
 *     <ToggleGroupItem value="list" aria-label="List view"><Icon …/></ToggleGroupItem>
 *   </ToggleGroup>
 *
 * Use it for switching how something is *displayed*. For switching what is
 * displayed, use Tabs — a tab implies a panel below it, and screen readers
 * announce the two differently.
 *
 * An icon-only item needs an `aria-label`; there is no visible text to fall
 * back on.
 */

const SizeContext =
  React.createContext<ToggleGroupItemRecipeProps['size']>('md');
const ShapeContext =
  React.createContext<ToggleGroupItemRecipeProps['shape']>('rounded');

/** So a group can set the appearance once instead of on every item. */
const VariantContext =
  React.createContext<ToggleGroupItemRecipeProps['variant']>('solid');

/**
 * Radix types Root as a union discriminated on `type`, which is what makes
 * `onValueChange` a string for "single" and a string[] for "multiple". Keeping
 * the union in the public type is the whole point — widening `type` to
 * `'single' | 'multiple'` silently degrades that callback to `any`.
 */
type RootProps = React.ComponentPropsWithoutRef<
  typeof ToggleGroupPrimitive.Root
>;

export type ToggleGroupProps = RootProps &
  ToggleGroupRecipeProps & {
    size?: ToggleGroupItemRecipeProps['size'];
    shape?: ToggleGroupItemRecipeProps['shape'];
    variant?: ToggleGroupItemRecipeProps['variant'];
  };

export const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  ToggleGroupProps
>(function ToggleGroup(
  {
    className,
    bordered,
    gap,
    wrap,
    shape = 'rounded',
    size = 'md',
    variant = 'solid',
    ...props
  },
  ref
) {
  return (
    <SizeContext.Provider value={size}>
      <ShapeContext.Provider value={shape}>
        <VariantContext.Provider value={variant}>
          <ToggleGroupPrimitive.Root
            ref={ref}
            className={cn(
              toggleGroupRecipe({ bordered, shape, gap, wrap }),
              className
            )}
            // Narrowed for the spread only; the union above is what callers see.
            {...(props as Extract<RootProps, { type: 'single' }>)}
          />
        </VariantContext.Provider>
      </ShapeContext.Provider>
    </SizeContext.Provider>
  );
});

export const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    ToggleGroupItemRecipeProps
>(function ToggleGroupItem({ className, size, shape, variant, ...props }, ref) {
  const inheritedSize = React.useContext(SizeContext);
  const inheritedShape = React.useContext(ShapeContext);
  const inheritedVariant = React.useContext(VariantContext);
  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        toggleGroupItemRecipe({
          size: size ?? inheritedSize,
          shape: shape ?? inheritedShape,
          variant: variant ?? inheritedVariant,
        }),
        className
      )}
      {...props}
    />
  );
});
